import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Media } from '@digilize/shared/definitions/src';
import { resizedTo } from '@plys/src/utils/media.utils';

export interface Favicons {
  favicon_light?: Media;
  favicon_dark?: Media;
}

const DEFAULT_FAVICON_LIGHT = 'assets/pauly-favico-light-48x48.svg';
const DEFAULT_FAVICON_DARK = 'assets/pauly-favico-dark-48x48.svg';

/** The largest a browser draws a tab icon at; the resizer is asked for twice it, for retina. */
const FAVICON_BOX = 32;

@Injectable({
  providedIn: 'root',
})
export class FaviconService {
  private faviconLight: Media = null;
  private faviconDark: Media = null;
  private readonly darkScheme = window.matchMedia('(prefers-color-scheme: dark)');

  constructor(@Inject(DOCUMENT) private dom: Document) {}

  init(): void {
    this.darkScheme.addEventListener('change', () => this.apply());
    this.apply();
  }

  /* favicons are configured per Point of Sale; if only one is set it is used for both color schemes */
  setFavicons({ favicon_light, favicon_dark }: Favicons): void {
    this.faviconLight = favicon_light ?? null;
    this.faviconDark = favicon_dark ?? null;
    this.apply();
  }

  private apply(): void {
    const prefersDark = this.darkScheme.matches;
    const custom = prefersDark ? this.faviconDark ?? this.faviconLight : this.faviconLight ?? this.faviconDark;
    const link = this.getLinkElement();

    if (custom) {
      if (custom.is_extension_vector) {
        link.href = custom.url1x;
        link.type = 'image/svg+xml';
      } else {
        /* A raster favicon is drawn at 16-32px, so serving the uploaded original is pure waste. */
        link.href = resizedTo(custom.url1x, FAVICON_BOX) ?? custom.url1x;
        link.removeAttribute('type');
      }
    } else {
      link.href = prefersDark ? DEFAULT_FAVICON_DARK : DEFAULT_FAVICON_LIGHT;
      link.type = 'image/svg+xml';
    }
  }

  private getLinkElement(): HTMLLinkElement {
    let link = this.dom.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = this.dom.createElement('link');
      link.rel = 'icon';
      this.dom.head.appendChild(link);
    }
    return link;
  }
}
